/**
 * ContactPersonSelector — Batch 3 · Fase 5 (Feb 2026)
 *
 * Toont een dropdown met alle beschikbare contactpersonen voor een klant.
 *   - Optie 1 = Primair contact (klant zelf: naam/email/telefoon)
 *   - Opties 2..N = Additional Contacts (naam · rol · e-mail/telefoon)
 *
 * Bij selectie patch de UI de party (sender/recipient/klant-blok) met de
 * relevante communicatievelden. customer_id blijft leidend en het adres
 * verandert NIET; alleen naam/email/telefoon.
 *
 * Verschijnt alleen ALS de klant additional_contacts heeft (>= 1). Bij een
 * klant zonder extra contacten valt dit component stilletjes weg zodat de
 * bestaande UI ongewijzigd blijft.
 *
 * Props:
 *  - customer        — de canonicalized klant-payload (uit CustomerAutofillPicker.onSelect)
 *  - value           — huidige selectie key ("primary" of "contact-{id}")
 *  - onSelect(key, contact) — callback: key + het contact-object om in party
 *                             te patchen (of null bij reset)
 *  - disabled        — readonly modus
 *  - testIdPrefix    — data-testid prefix (default: "contact-person-selector")
 */
import React, { useMemo } from "react";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { buildContactOptions } from "../../services/customerAutofillService";
import { UserRound } from "lucide-react";

export default function ContactPersonSelector({
  customer,
  value,
  onSelect,
  disabled = false,
  testIdPrefix = "contact-person-selector",
  label = "Contactpersoon",
  hint = null,
  alwaysVisible = false,
}) {
  const options = useMemo(() => buildContactOptions(customer), [customer]);

  // Alleen renderen als er extra contacten zijn — anders is een selector overbodig.
  if (!options || (!alwaysVisible && options.length <= 1)) return null;

  const handleChange = (newKey) => {
    const opt = options.find((o) => o.key === newKey);
    if (opt) onSelect?.(newKey, opt.contact);
  };

  return (
    <div className="mb-3" data-testid={`${testIdPrefix}-container`}>
      <Label className="text-[10px] uppercase font-bold tracking-wide text-gray-600 flex items-center gap-1">
        <UserRound className="w-3 h-3 text-[#0055FF]" /> {label}
      </Label>
      <Select value={value || "primary"} onValueChange={handleChange} disabled={disabled}>
        <SelectTrigger className="rounded-none mt-1" data-testid={`${testIdPrefix}-trigger`}>
          <SelectValue placeholder="Kies contactpersoon" />
        </SelectTrigger>
        <SelectContent data-testid={`${testIdPrefix}-options`}>
          {options.map((opt) => (
            <SelectItem
              key={opt.key}
              value={opt.key}
              data-testid={`${testIdPrefix}-option-${opt.key}`}
            >
              <div className="flex flex-col">
                <span className="font-medium">{opt.label}{opt.isPrimary ? " (primair)" : ""}</span>
                {opt.sublabel && (
                  <span className="text-[10px] text-gray-500">{opt.sublabel}</span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint && <div className="text-[10px] text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}
